import { useFormContext } from 'react-hook-form'
import { useCallback, useMemo } from 'react'
import get from 'lodash/get'

import { EducationHostedFormModel } from '../EducationHostedForm.types'

import { getInputStatus } from '@percent/lemonade'

export type HasCurrentPageErrorsProps = Array<{ fieldKey: keyof EducationHostedFormModel; isOptional?: boolean }>

export const useHasCurrentPageErrors = (fields: HasCurrentPageErrorsProps) => {
  const { formState, getValues } = useFormContext<EducationHostedFormModel>()

  const hasValue = useCallback((value: null | undefined | string | Date) => {
    if (value instanceof Date) {
      return !isNaN(value.getTime())
    }

    if ([null, undefined, ''].includes(value)) {
      return false
    }

    return true
  }, [])

  const hasPageErrors = useMemo(
    () =>
      fields.some(({ fieldKey, isOptional }) => {
        const fieldValue = getValues(fieldKey)

        return !!formState.errors[fieldKey]?.message || (isOptional ? false : !hasValue(fieldValue))
      }),
    [fields, formState.errors, getValues, hasValue]
  )

  const errors = useMemo(
    () =>
      fields.reduce<Partial<Record<keyof EducationHostedFormModel, string | undefined>>>(
        (accErrors, { fieldKey: currFieldKey }) => {
          const shouldShowError = get(formState.touchedFields, currFieldKey)
          const fieldError = shouldShowError && get(formState.errors, currFieldKey)

          if (fieldError) {
            return { ...accErrors, [currFieldKey]: fieldError.message }
          }

          return accErrors
        },
        {}
      ),
    [fields, formState.errors, formState.touchedFields]
  )

  return { hasPageErrors, errors, getStatus: getInputStatus }
}
